import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { toast } from "sonner";
import { Modal } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import { createCycle, getAllLevelTypes } from "../../../api/settings";
import { Select, Switch } from "antd";
import { useEffect, useState } from "react";

type Props = {
  show: boolean;
  onClose: () => void;
  onSaved: () => void;
};

type LevelTypeOption = {
  id: string;
  label: string;
  last: boolean;
};

const CreateCycleModal = ({ show, onClose, onSaved }: Props) => {
  const [allLevelType, setAllLevelType] = useState<LevelTypeOption[]>([]);
  const [selectedLevelType, setSelectedLevelType] = useState<LevelTypeOption[]>(
    [],
  );

  const createSchema = z.object({
    label: z.string().nonempty("Libellé requis"),
    diploma_name: z.string().nonempty("Libellé du diplome requis"),
    diploma_abbreviation: z.string().nonempty("Abbreviation du diplôme requis"),
    duration: z.string().nonempty("Nombre d'anné requis"),
    typeLevels: z
      .array(
        z.object({
          id: z.string(),
          last: z.boolean(),
        }),
      )
      .min(1, "Les types de niveau sont requis")
      .refine(
        (typeLevels) => typeLevels.filter((item) => item.last).length === 1,
        "Un seul type de niveau doit être défini comme dernier",
      ),
  });

  type FormFields = z.infer<typeof createSchema>;
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(createSchema),
  });

  //Traitement après validation du formulaire de création d'un type de niveau
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      //Envoi des datas pour création vers le backend
      await createCycle(data);

      reset(); //On réinitialise les champs du formulaire
      setSelectedLevelType([]);
      if (onSaved) onSaved(); // On fait un reload de la liste

      onClose(); // On ferme le modal
      toast.success("Cycle créé !"); //Toast de succès
    } catch (error: any) {
      // console.log(error)
      // Si erreur (Toast Error)
      toast.error(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  // Fonction pour récupérer les enseignants
  const fetchLevelType = async () => {
    try {
      const levelTypeData = await getAllLevelTypes();

      setAllLevelType(levelTypeData.data);
    } catch (error: any) {
      // console.log("erreur récupération des cycle ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    if (!show) return;
    fetchLevelType();
  }, [show]);

  useEffect(() => {
    setValue(
      "typeLevels",
      selectedLevelType?.map((cls) => ({ id: cls.id, last: cls.last })) || [],
      { shouldValidate: true },
    );
  }, [selectedLevelType, setValue]);

  const handleLevelTypeChange = (ids: string[]) => {
    const previousSelection = selectedLevelType;
    const selected = allLevelType
      .filter((lvl) => ids.includes(lvl.id))
      .map((lvl) => {
        const existing = previousSelection.find((item) => item.id === lvl.id);
        return {
          ...lvl,
          last: existing?.last ?? false,
        };
      });

    if (selected.length > 0 && !selected.some((item) => item.last)) {
      selected[0].last = true;
    }

    setSelectedLevelType(selected);
  };

  const handleLastToggle = (id: string, checked: boolean) => {
    setSelectedLevelType((prev) => {
      if (checked) {
        return prev.map((item) => ({
          ...item,
          last: item.id === id,
        }));
      }

      const target = prev.find((item) => item.id === id);
      if (!target?.last) {
        return prev;
      }

      const fallback = prev.find((item) => item.id !== id);
      if (!fallback) {
        return prev;
      }

      return prev.map((item) => ({
        ...item,
        last: item.id === fallback.id,
      }));
    });
  };

  const handleClose = () => {
    reset();
    setSelectedLevelType([]);
    onClose();
  };

  return (
    <>
      <Modal show={show} onHide={() => onClose()}>
        <Modal.Header closeButton>
          <Modal.Title>Nouveau Cycle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form
            id="academic-year-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row row-cols-xxl-5 row-cols-md-6">
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Libellé
                  </label>
                  <input
                    {...register("label")}
                    type="text"
                    className={
                      errors.label ? "form-control is-invalid" : "form-control"
                    }
                    placeholder="Ex: Doctorat"
                  />
                  {errors.label && (
                    <div className="invalid-feedback">
                      {errors.label.message}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-xxl-9 col-xl-9 col-md-9">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Diplôme
                  </label>
                  <input
                    {...register("diploma_name")}
                    type="text"
                    className={
                      errors.diploma_name
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Ex: Licence Professionnelle"
                  />
                  {errors.diploma_name && (
                    <div className="invalid-feedback">
                      {errors.diploma_name.message}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-xxl-3 col-xl-3 col-md-3">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Abbreviation
                  </label>
                  <input
                    {...register("diploma_abbreviation")}
                    type="text"
                    className={
                      errors.diploma_abbreviation
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Ex: LP"
                  />
                  {errors.diploma_abbreviation && (
                    <div className="invalid-feedback">
                      {errors.diploma_abbreviation.message}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Durée du cycle
                  </label>
                  <input
                    {...register("duration")}
                    type="number"
                    className={
                      errors.duration
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                    placeholder="Ex: 3ans"
                  />
                  {errors.duration && (
                    <div className="invalid-feedback">
                      {errors.duration.message}
                    </div>
                  )}
                </div>
              </div>
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label" htmlFor="validationServer01">
                    Types de niveau
                  </label>
                  <Select
                    mode="multiple"
                    showSearch
                    allowClear
                    size="large"
                    optionFilterProp="label"
                    removeIcon={<span style={{ fontSize: 12 }}>×</span>}
                    style={{ width: "100%" }}
                    getPopupContainer={(trigger) =>
                      trigger.parentElement as HTMLElement
                    }
                    dropdownStyle={{ zIndex: 2000 }}
                    placeholder="Sélectionner les classes"
                    value={
                      Array.isArray(selectedLevelType)
                        ? selectedLevelType.map((lvl) => lvl.id)
                        : []
                    }
                    onChange={handleLevelTypeChange}
                    onClear={() => setSelectedLevelType([])}
                  >
                    {allLevelType.map((lvl) => (
                      <Select.Option
                        key={lvl.id}
                        value={lvl.id}
                        label={`${lvl.label ?? ""}`}
                      >
                        {lvl.label}
                      </Select.Option>
                    ))}
                  </Select>
                  {errors.typeLevels && (
                    <div className="invalid-feedback d-block">
                      {errors.typeLevels.message}
                    </div>
                  )}
                </div>
              </div>
              {selectedLevelType.length > 0 && (
                <div className="col-xxl-12 col-xl-12 col-md-12">
                  <div className="border rounded p-3 bg-light">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">Types de niveau sélectionnés</h6>
                      <small className="text-muted">
                        Un seul doit être le dernier
                      </small>
                    </div>
                    <div className="d-flex flex-column gap-2">
                      {selectedLevelType.map((lvl) => (
                        <div
                          key={lvl.id}
                          className="d-flex justify-content-between align-items-center bg-white border rounded px-3 py-2"
                        >
                          <div>
                            <div className="fw-medium">{lvl.label}</div>
                            {lvl.last && (
                              <small className="text-success">
                                Dernier niveau du cycle
                              </small>
                            )}
                          </div>
                          <Switch
                            checked={lvl.last}
                            onChange={(checked) =>
                              handleLastToggle(lvl.id, checked)
                            }
                            checkedChildren="Fin"
                            unCheckedChildren=""
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={handleClose}
          >
            Annuler
          </button>

          {isSubmitting ? (
            <button
              className="btn btn-primary-light"
              type="button"
              disabled={false}
            >
              <span
                className="spinner-border spinner-border-sm align-middle me-1"
                role="status"
                aria-hidden="true"
              />
              Chargement...
            </button>
          ) : (
            <button
              type="submit"
              form="academic-year-form" // 🔗 liaison au formulaire
              className="btn btn-primary"
              disabled={isSubmitting}
            >
              Enregistrer
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withPermission(
  CreateCycleModal,
  [buildPermission(Resource.ACADEMIC_YEAR, Action.CREATE)],
  "masquer",
);
